/**
 * BookAdminPanel — CRUD for the digital book:
 *   Volume → Section → Subsection → BookPage (layouts A–F).
 *
 * Left column: 3-level tree with drag reordering (native HTML5 DnD).
 * Right column: editor for the currently-selected node.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  adminListVolumes, adminCreateVolume, adminUpdateVolume, adminDeleteVolume,
  adminListSections, adminCreateSection, adminUpdateSection, adminDeleteSection,
  adminListSubsections, adminCreateSubsection, adminUpdateSubsection, adminDeleteSubsection,
  adminListPages, adminGetPage, adminCreatePage, adminUpdatePage, adminDeletePage,
  adminReorder, uploadBookImage, PAGE_LAYOUTS,
} from "@/lib/bookApi";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronRight, ChevronDown, Plus, Trash2, Image as ImageIcon, X, GripVertical } from "lucide-react";

const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const resolveMedia = (u) => (u ? (u.startsWith("http") ? u : `${backend}${u}`) : null);

function slugify(text) {
  return (text || "")
    .toString().toLowerCase().trim()
    .replace(/\s+/g, "-")
    .replace(/[^\wа-я\-]/gi, "")
    .slice(0, 80) || `p-${Math.random().toString(16).slice(2, 8)}`;
}

/**
 * Interpret `local` (as if it was typed in Europe/Vilnius) → UTC ISO string.
 * Uses IANA-aware conversion via Intl.DateTimeFormat trick.
 */
function vilniusLocalToUtcIso(local) {
  if (!local) return null;
  // local like "2026-02-25T10:00"
  const [datePart, timePart] = local.split("T");
  if (!datePart || !timePart) return null;
  const [Y, Mo, D] = datePart.split("-").map(Number);
  const [H, Mi] = timePart.split(":").map(Number);
  // Try candidate UTC by assuming Vilnius offset; refine using DateTimeFormat.
  const guess = new Date(Date.UTC(Y, Mo - 1, D, H, Mi, 0));
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Vilnius", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(guess).map((p) => [p.type, p.value]));
  const vilniusOfGuess = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, 0);
  const wantedUTC = Date.UTC(Y, Mo - 1, D, H, Mi, 0);
  const delta = wantedUTC - vilniusOfGuess;
  return new Date(guess.getTime() + delta).toISOString();
}

function utcIsoToVilniusLocal(utcIso) {
  if (!utcIso) return "";
  const d = new Date(utcIso);
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Vilnius", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(d).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

// ---------- Focal-point image picker ----------
function ImagePicker({ value, onChange, testid, aspect = "aspect-[16/10]" }) {
  const [uploading, setUploading] = useState(false);
  const v = value || {};
  const url = v.url || (v.media_id ? `/api/media/files/${v.media_id}` : null);
  const src = resolveMedia(url);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const m = await uploadBookImage(f);
      onChange({
        ...(v || {}),
        media_id: m.id,
        url: m.file_url,
        focal_x: v.focal_x ?? 0.5,
        focal_y: v.focal_y ?? 0.5,
      });
      toast.success("Изображение загружено");
    } catch (err) {
      toast.error("Не удалось загрузить: " + (err?.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const setFocal = (evt) => {
    if (!src) return;
    const rect = evt.currentTarget.getBoundingClientRect();
    const x = (evt.clientX - rect.left) / rect.width;
    const y = (evt.clientY - rect.top) / rect.height;
    onChange({ ...(v || {}), focal_x: Math.max(0, Math.min(1, x)), focal_y: Math.max(0, Math.min(1, y)) });
  };

  return (
    <div className="border border-neutral-200 rounded p-3 bg-white/50" data-testid={testid}>
      {src ? (
        <div className={`relative w-full ${aspect} overflow-hidden rounded bg-neutral-100 cursor-crosshair`}
             onClick={setFocal} title="Кликните, чтобы задать фокус">
          <img src={src} alt="" className="w-full h-full object-cover"
               style={{ objectPosition: `${(v.focal_x ?? 0.5) * 100}% ${(v.focal_y ?? 0.5) * 100}%` }} />
          <div className="absolute w-5 h-5 border-2 border-white rounded-full shadow"
               style={{
                 left: `${(v.focal_x ?? 0.5) * 100}%`, top: `${(v.focal_y ?? 0.5) * 100}%`,
                 transform: "translate(-50%,-50%)", background: "rgba(0,0,0,0.4)",
               }}/>
        </div>
      ) : (
        <div className={`w-full ${aspect} bg-neutral-100 flex items-center justify-center text-neutral-400 rounded`}>
          <ImageIcon className="w-6 h-6" />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 rounded border border-neutral-300 text-sm">
          <input type="file" accept="image/*" className="hidden" onChange={onFile}
                 data-testid={`${testid}-file`} />
          {uploading ? "Загрузка…" : (src ? "Заменить" : "Загрузить")}
        </label>
        {src && (
          <button className="text-sm opacity-60 hover:opacity-100"
            onClick={() => onChange(null)}
            data-testid={`${testid}-clear`}>Убрать</button>
        )}
        <span className="text-xs opacity-60">
          Фокус: {Math.round((v.focal_x ?? 0.5) * 100)}% · {Math.round((v.focal_y ?? 0.5) * 100)}%
        </span>
      </div>
      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
        <Input placeholder="Alt (для доступности)" value={v.alt || ""}
          onChange={(e) => onChange({ ...(v || {}), alt: e.target.value })} />
        <Input placeholder="Подпись (caption)" value={v.caption || ""}
          onChange={(e) => onChange({ ...(v || {}), caption: e.target.value })} />
      </div>
    </div>
  );
}

// ---------- Table editor (Layout F) ----------
function TableEditor({ value, onChange }) {
  const t = value || { intro: "", headers: [], rows: [], outro: "", conclusion: "" };
  const setH = (i, v) => {
    const headers = [...t.headers]; headers[i] = v;
    onChange({ ...t, headers });
  };
  const addCol = () => onChange({ ...t, headers: [...t.headers, "Колонка"], rows: t.rows.map((r) => [...r, ""]) });
  const delCol = (i) => onChange({
    ...t, headers: t.headers.filter((_, k) => k !== i),
    rows: t.rows.map((r) => r.filter((_, k) => k !== i)),
  });
  const addRow = () => onChange({ ...t, rows: [...t.rows, t.headers.map(() => "")] });
  const delRow = (i) => onChange({ ...t, rows: t.rows.filter((_, k) => k !== i) });
  const setCell = (ri, ci, v) => {
    const rows = t.rows.map((r) => r.slice());
    rows[ri][ci] = v; onChange({ ...t, rows });
  };
  const moveRow = (ri, dir) => {
    const nri = ri + dir;
    if (nri < 0 || nri >= t.rows.length) return;
    const rows = t.rows.slice();
    [rows[ri], rows[nri]] = [rows[nri], rows[ri]];
    onChange({ ...t, rows });
  };

  return (
    <div className="border border-neutral-200 rounded p-3 bg-white/60 space-y-3"
         data-testid="book-admin-table-editor">
      <div>
        <label className="block text-xs mb-1 opacity-70">Вводный текст (до таблицы)</label>
        <textarea value={t.intro || ""}
          onChange={(e) => onChange({ ...t, intro: e.target.value })}
          rows={2} className="w-full border rounded px-2 py-1 text-sm" />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className="w-8"></th>
              {t.headers.map((h, i) => (
                <th key={i} className="p-1">
                  <div className="flex items-center gap-1">
                    <Input value={h} onChange={(e) => setH(i, e.target.value)}
                      className="h-8 text-sm" data-testid={`book-admin-table-header-${i}`} />
                    <button onClick={() => delCol(i)} className="p-1 opacity-60 hover:opacity-100"
                      data-testid={`book-admin-table-delcol-${i}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </th>
              ))}
              <th>
                <button onClick={addCol}
                  className="p-1 border rounded"
                  data-testid="book-admin-table-addcol">
                  <Plus className="w-4 h-4" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {t.rows.map((row, ri) => (
              <tr key={ri}>
                <td className="align-top p-1">
                  <div className="flex flex-col items-center gap-0.5">
                    <button onClick={() => moveRow(ri, -1)}
                      className="p-0.5 opacity-60 hover:opacity-100">↑</button>
                    <button onClick={() => moveRow(ri, 1)}
                      className="p-0.5 opacity-60 hover:opacity-100">↓</button>
                    <button onClick={() => delRow(ri)}
                      className="p-0.5 opacity-60 hover:opacity-100 text-red-600"
                      data-testid={`book-admin-table-delrow-${ri}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
                {row.map((cell, ci) => (
                  <td key={ci} className="p-1 align-top">
                    <textarea value={cell} rows={1}
                      onChange={(e) => setCell(ri, ci, e.target.value)}
                      className="w-full border rounded px-2 py-1 text-sm resize-y" />
                  </td>
                ))}
                <td />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <Button variant="outline" size="sm" onClick={addRow}
          data-testid="book-admin-table-addrow">
          <Plus className="w-4 h-4 mr-1" /> Добавить строку
        </Button>
      </div>
      <div>
        <label className="block text-xs mb-1 opacity-70">Текст после таблицы</label>
        <textarea value={t.outro || ""}
          onChange={(e) => onChange({ ...t, outro: e.target.value })}
          rows={2} className="w-full border rounded px-2 py-1 text-sm" />
      </div>
      <div>
        <label className="block text-xs mb-1 opacity-70">«Что запомнить»</label>
        <textarea value={t.conclusion || ""}
          onChange={(e) => onChange({ ...t, conclusion: e.target.value })}
          rows={3} className="w-full border rounded px-2 py-1 text-sm" />
      </div>
    </div>
  );
}

// ---------- Recipe editor (Layout E) ----------
function RecipeEditor({ value, onChange }) {
  const r = value || { ingredient_groups: [] };
  const set = (patch) => onChange({ ...r, ...patch });
  const addGroup = () => set({
    ingredient_groups: [...(r.ingredient_groups || []), { title: "", items: [] }]
  });
  const setGroup = (gi, patch) => {
    const groups = (r.ingredient_groups || []).slice();
    groups[gi] = { ...groups[gi], ...patch };
    set({ ingredient_groups: groups });
  };
  const delGroup = (gi) => set({ ingredient_groups: (r.ingredient_groups || []).filter((_, k) => k !== gi) });
  const addItem = (gi) => {
    const groups = (r.ingredient_groups || []).slice();
    groups[gi].items = [...(groups[gi].items || []), { name: "", amount: "", unit: "" }];
    set({ ingredient_groups: groups });
  };
  const setItem = (gi, ii, patch) => {
    const groups = (r.ingredient_groups || []).slice();
    const items = groups[gi].items.slice();
    items[ii] = { ...items[ii], ...patch };
    groups[gi] = { ...groups[gi], items };
    set({ ingredient_groups: groups });
  };
  const delItem = (gi, ii) => {
    const groups = (r.ingredient_groups || []).slice();
    groups[gi].items = groups[gi].items.filter((_, k) => k !== ii);
    set({ ingredient_groups: groups });
  };

  const TA = (key, label, rows = 3) => (
    <div>
      <label className="block text-xs opacity-70 mb-1">{label}</label>
      <textarea value={r[key] || ""} rows={rows}
        onChange={(e) => set({ [key]: e.target.value })}
        className="w-full border rounded px-2 py-1.5 text-sm"
        data-testid={`book-admin-recipe-${key}`} />
    </div>
  );

  return (
    <div className="border border-neutral-200 rounded p-3 bg-white/60 space-y-3"
         data-testid="book-admin-recipe-editor">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Input placeholder="Выход (напр. «12 порций»)" value={r.yield_text || ""}
          onChange={(e) => set({ yield_text: e.target.value })} />
        <Input placeholder="Форма (напр. «Ø 20 см»)" value={r.form_text || ""}
          onChange={(e) => set({ form_text: e.target.value })} />
      </div>
      {TA("intro", "Введение / назначение", 2)}

      <div className="border-t pt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium">Ингредиенты</div>
          <Button size="sm" variant="outline" onClick={addGroup}
            data-testid="book-admin-recipe-addgroup">
            <Plus className="w-4 h-4 mr-1" /> Группа
          </Button>
        </div>
        {(r.ingredient_groups || []).map((g, gi) => (
          <div key={gi} className="border rounded p-2 mb-2 bg-white/70">
            <div className="flex items-center gap-2 mb-2">
              <Input placeholder="Название группы (необязательно)" value={g.title || ""}
                onChange={(e) => setGroup(gi, { title: e.target.value })} />
              <button onClick={() => delGroup(gi)} className="text-red-600">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            {(g.items || []).map((it, ii) => (
              <div key={ii} className="grid grid-cols-[1fr_auto_auto_auto] gap-1 mb-1">
                <Input placeholder="Название" value={it.name || ""}
                  onChange={(e) => setItem(gi, ii, { name: e.target.value })} />
                <Input placeholder="Кол-во" value={it.amount || ""} className="w-24"
                  onChange={(e) => setItem(gi, ii, { amount: e.target.value })} />
                <Input placeholder="Ед." value={it.unit || ""} className="w-20"
                  onChange={(e) => setItem(gi, ii, { unit: e.target.value })} />
                <button onClick={() => delItem(gi, ii)}
                  className="text-red-500 px-2">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <Button size="sm" variant="ghost" onClick={() => addItem(gi)}>
              <Plus className="w-4 h-4 mr-1" /> Ингредиент
            </Button>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {TA("preparation", "Подготовка")}
        {TA("technology", "Технология", 5)}
        {TA("temperatures", "Температуры", 2)}
        {TA("baking", "Выпекание", 2)}
        {TA("cooling", "Охлаждение", 2)}
        {TA("assembly", "Сборка", 2)}
        {TA("soaking", "Пропитка", 2)}
        {TA("storage", "Хранение", 2)}
        {TA("freezing", "Заморозка / разморозка", 2)}
        {TA("substitutions", "Замены", 2)}
        {TA("common_mistakes", "Частые ошибки", 3)}
        {TA("lab_recommends", "Лаборатория рекомендует", 2)}
        {TA("technologist_advice", "Совет технолога", 2)}
        {TA("remember", "Что важно запомнить", 2)}
      </div>
    </div>
  );
}

// ---------- Draggable list row ----------
function TreeRow({ label, sub, active, onSelect, onDragStart, onDragOver, onDrop, kind, id, statusBadge, indent = 0 }) {
  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.setData("text/id", id); e.dataTransfer.setData("text/kind", kind); onDragStart?.(id, kind); }}
      onDragOver={(e) => { e.preventDefault(); onDragOver?.(id, kind); }}
      onDrop={(e) => { e.preventDefault(); onDrop?.(e.dataTransfer.getData("text/id"), id, kind); }}
      onClick={onSelect}
      className={`group flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm
        ${active ? "bg-espresso/10 text-espresso" : "hover:bg-neutral-100"}`}
      style={{ marginLeft: indent * 14 }}
      data-testid={`book-admin-tree-${kind}-${id}`}
    >
      <GripVertical className="w-3.5 h-3.5 opacity-30 group-hover:opacity-70 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="truncate">{label}</div>
        {sub && <div className="text-[10px] opacity-60 truncate">{sub}</div>}
      </div>
      {statusBadge && (
        <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${
          statusBadge === "published" ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-600"
        }`}>{statusBadge === "published" ? "PUB" : "DRAFT"}</span>
      )}
    </div>
  );
}

// ---------- Main Panel ----------
export default function BookAdminPanel({ productIdFilter } = {}) {
  const [volumes, setVolumes] = useState([]);
  const [sections, setSections] = useState([]);
  const [subsections, setSubsections] = useState([]);
  const [pages, setPages] = useState([]);
  const [openVol, setOpenVol] = useState({});
  const [openSec, setOpenSec] = useState({});
  const [openSub, setOpenSub] = useState({});
  const [selected, setSelected] = useState(null); // { kind, id }
  const [editor, setEditor] = useState(null);
  const [savingBusy, setSavingBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [v, s, ss, p] = await Promise.all([
        adminListVolumes(),
        adminListSections(),
        adminListSubsections(),
        adminListPages(),
      ]);
      // If a product filter is provided (Product Workspace), scope every entity to
      // volumes bound to that product only. This is the frontend guard; backend
      // already enforces via product_scope_ids.
      let volsScoped = v;
      if (productIdFilter) {
        volsScoped = (v || []).filter((x) => x.product_id === productIdFilter);
      }
      const volIds = new Set(volsScoped.map((x) => x.id));
      const secsScoped = productIdFilter ? (s || []).filter((x) => volIds.has(x.volume_id)) : s;
      const subsScoped = productIdFilter ? (ss || []).filter((x) => volIds.has(x.volume_id)) : ss;
      const pagesScoped = productIdFilter ? (p || []).filter((x) => volIds.has(x.volume_id)) : p;
      setVolumes(volsScoped); setSections(secsScoped); setSubsections(subsScoped); setPages(pagesScoped);
      if (volsScoped.length && !openVol[volsScoped[0].id]) setOpenVol((o) => ({ ...o, [volsScoped[0].id]: true }));
    } catch (e) {
      toast.error("Не удалось загрузить структуру: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [productIdFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadAll(); }, [loadAll]);

  // When selection changes, load full editor state (for pages we hit adminGetPage).
  useEffect(() => {
    if (!selected) { setEditor(null); return; }
    if (selected.kind === "page") {
      adminGetPage(selected.id).then((p) => {
        // Precompute Vilnius-local field for the datetime picker.
        setEditor({ ...p, _publish_at_local: utcIsoToVilniusLocal(p.publish_at) });
      }).catch(() => setEditor(null));
    } else if (selected.kind === "volume") {
      const v = volumes.find((x) => x.id === selected.id);
      setEditor(v ? { ...v } : null);
    } else if (selected.kind === "section") {
      const s = sections.find((x) => x.id === selected.id);
      setEditor(s ? { ...s } : null);
    } else if (selected.kind === "subsection") {
      const s = subsections.find((x) => x.id === selected.id);
      setEditor(s ? { ...s } : null);
    }
  }, [selected, volumes, sections, subsections]);

  // ---------- Tree helpers ----------
  const secsByVol = useMemo(() => {
    const m = {};
    sections.forEach((s) => { (m[s.volume_id] ||= []).push(s); });
    Object.values(m).forEach((a) => a.sort((x, y) => x.sort_order - y.sort_order));
    return m;
  }, [sections]);
  const subsBySec = useMemo(() => {
    const m = {};
    subsections.forEach((s) => { (m[s.section_id] ||= []).push(s); });
    Object.values(m).forEach((a) => a.sort((x, y) => x.sort_order - y.sort_order));
    return m;
  }, [subsections]);
  const pagesByParent = useMemo(() => {
    const m = {};
    pages.forEach((p) => { (m[p.parent_id] ||= []).push(p); });
    Object.values(m).forEach((a) => a.sort((x, y) => x.sort_order - y.sort_order));
    return m;
  }, [pages]);

  // ---------- Create / delete ----------
  const createVolume = async () => {
    const title = window.prompt("Название нового тома?");
    if (!title) return;
    const slug = slugify(title);
    try {
      await adminCreateVolume({ title, slug, status: "draft", sort_order: volumes.length });
      toast.success("Том создан");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не удалось создать том");
    }
  };
  const createSection = async (volume_id) => {
    const title = window.prompt("Название раздела?");
    if (!title) return;
    const slug = slugify(title);
    const arr = secsByVol[volume_id] || [];
    try {
      await adminCreateSection({ volume_id, title, slug, status: "draft", sort_order: arr.length });
      toast.success("Раздел создан"); loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не удалось");
    }
  };
  const createSubsection = async (section_id, volume_id) => {
    const title = window.prompt("Название подраздела?");
    if (!title) return;
    const slug = slugify(title);
    const arr = subsBySec[section_id] || [];
    try {
      await adminCreateSubsection({ volume_id, section_id, title, slug, status: "draft", sort_order: arr.length });
      toast.success("Подраздел создан"); loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не удалось");
    }
  };
  const createPage = async ({ parent_kind, parent_id, volume_id }) => {
    const title = window.prompt("Название новой страницы?");
    if (!title) return;
    const arr = pagesByParent[parent_id] || [];
    try {
      const p = await adminCreatePage({
        volume_id, parent_kind, parent_id,
        title, slug: slugify(title), layout: "A", status: "draft",
        sort_order: arr.length,
      });
      toast.success("Страница создана"); await loadAll();
      setSelected({ kind: "page", id: p.id });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не удалось");
    }
  };

  const removeNode = async () => {
    if (!selected) return;
    if (!window.confirm("Удалить безвозвратно?")) return;
    try {
      if (selected.kind === "volume") await adminDeleteVolume(selected.id);
      if (selected.kind === "section") await adminDeleteSection(selected.id);
      if (selected.kind === "subsection") await adminDeleteSubsection(selected.id);
      if (selected.kind === "page") await adminDeletePage(selected.id);
      toast.success("Удалено");
      setSelected(null); loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не удалось");
    }
  };

  // ---------- Save editor ----------
  const saveEditor = async () => {
    if (!editor || !selected) return;
    setSavingBusy(true);
    try {
      const payload = { ...editor };
      delete payload._publish_at_local;
      if (selected.kind === "volume")     await adminUpdateVolume(selected.id, payload);
      if (selected.kind === "section")    await adminUpdateSection(selected.id, payload);
      if (selected.kind === "subsection") await adminUpdateSubsection(selected.id, payload);
      if (selected.kind === "page")       await adminUpdatePage(selected.id, payload);
      toast.success("Сохранено");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ошибка сохранения");
    } finally {
      setSavingBusy(false);
    }
  };

  // ---------- Drag reorder ----------
  const onDrop = async (srcId, dstId, kind) => {
    if (!srcId || srcId === dstId) return;
    let list = [];
    if (kind === "section") {
      const vid = sections.find((s) => s.id === srcId)?.volume_id;
      list = (secsByVol[vid] || []).slice();
    } else if (kind === "subsection") {
      const sid = subsections.find((s) => s.id === srcId)?.section_id;
      list = (subsBySec[sid] || []).slice();
    } else if (kind === "page") {
      const parent = pages.find((p) => p.id === srcId)?.parent_id;
      list = (pagesByParent[parent] || []).slice();
    } else if (kind === "volume") {
      list = volumes.slice();
    }
    const from = list.findIndex((x) => x.id === srcId);
    const to = list.findIndex((x) => x.id === dstId);
    if (from < 0 || to < 0) return;
    list.splice(to, 0, list.splice(from, 1)[0]);
    const items = list.map((x, i) => ({ id: x.id, sort_order: i }));
    try {
      await adminReorder(kind, items);
      toast.success("Порядок обновлён"); loadAll();
    } catch (e) {
      toast.error("Не удалось изменить порядок");
    }
  };

  if (loading) return <div className="opacity-60 text-sm">Загрузка структуры книги…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6" data-testid="book-admin-panel">
      {/* Tree */}
      <div className="border border-neutral-200 rounded-lg bg-white/60 p-3 h-[76vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="font-serif text-lg">Библиотека</div>
          <Button size="sm" onClick={createVolume} data-testid="book-admin-new-volume">
            <Plus className="w-4 h-4 mr-1" /> Том
          </Button>
        </div>
        {volumes.map((v) => (
          <div key={v.id} className="mb-1">
            <div className="flex items-center gap-1">
              <button onClick={() => setOpenVol((o) => ({ ...o, [v.id]: !o[v.id] }))}>
                {openVol[v.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              <TreeRow
                kind="volume" id={v.id} label={v.title} sub={v.slug}
                statusBadge={v.status} active={selected?.kind === "volume" && selected.id === v.id}
                onSelect={() => setSelected({ kind: "volume", id: v.id })}
                onDrop={onDrop}
              />
              <button onClick={() => createSection(v.id)} className="p-1 opacity-60 hover:opacity-100"
                title="Добавить раздел" data-testid={`book-admin-new-section-${v.id}`}>
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
            {openVol[v.id] && (secsByVol[v.id] || []).map((s) => (
              <div key={s.id}>
                <div className="flex items-center gap-1" style={{ marginLeft: 14 }}>
                  <button onClick={() => setOpenSec((o) => ({ ...o, [s.id]: !o[s.id] }))}>
                    {openSec[s.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <TreeRow
                    kind="section" id={s.id} label={s.title} sub={s.slug}
                    statusBadge={s.status}
                    active={selected?.kind === "section" && selected.id === s.id}
                    onSelect={() => setSelected({ kind: "section", id: s.id })}
                    onDrop={onDrop}
                  />
                  <button onClick={() => createSubsection(s.id, v.id)} className="p-1 opacity-60 hover:opacity-100" title="Подраздел">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => createPage({ parent_kind: "section", parent_id: s.id, volume_id: v.id })}
                    className="p-1 opacity-60 hover:opacity-100 text-espresso" title="Страница">
                    +P
                  </button>
                </div>
                {openSec[s.id] && (pagesByParent[s.id] || []).map((p) => (
                  <TreeRow key={p.id}
                    kind="page" id={p.id}
                    label={`${p.title}`} sub={`layout ${p.layout}`}
                    indent={2}
                    statusBadge={p.status}
                    active={selected?.kind === "page" && selected.id === p.id}
                    onSelect={() => setSelected({ kind: "page", id: p.id })}
                    onDrop={onDrop}
                  />
                ))}
                {openSec[s.id] && (subsBySec[s.id] || []).map((sub) => (
                  <div key={sub.id}>
                    <div className="flex items-center gap-1" style={{ marginLeft: 28 }}>
                      <button onClick={() => setOpenSub((o) => ({ ...o, [sub.id]: !o[sub.id] }))}>
                        {openSub[sub.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      <TreeRow
                        kind="subsection" id={sub.id} label={sub.title} sub={sub.slug}
                        statusBadge={sub.status}
                        active={selected?.kind === "subsection" && selected.id === sub.id}
                        onSelect={() => setSelected({ kind: "subsection", id: sub.id })}
                        onDrop={onDrop}
                      />
                      <button onClick={() => createPage({ parent_kind: "subsection", parent_id: sub.id, volume_id: v.id })}
                        className="p-1 opacity-60 hover:opacity-100 text-espresso" title="Страница">
                        +P
                      </button>
                    </div>
                    {openSub[sub.id] && (pagesByParent[sub.id] || []).map((p) => (
                      <TreeRow key={p.id}
                        kind="page" id={p.id}
                        label={p.title} sub={`layout ${p.layout}`}
                        indent={3}
                        statusBadge={p.status}
                        active={selected?.kind === "page" && selected.id === p.id}
                        onSelect={() => setSelected({ kind: "page", id: p.id })}
                        onDrop={onDrop}
                      />
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Editor */}
      <div className="border border-neutral-200 rounded-lg bg-white/60 p-4 h-[76vh] overflow-y-auto">
        {!editor && (
          <div className="opacity-60 text-sm">
            Выберите том, раздел, подраздел или страницу слева, чтобы начать редактирование.
          </div>
        )}
        {editor && selected && (
          <>
            <div className="flex items-center justify-between mb-4 sticky top-0 bg-white/80 backdrop-blur pt-1 pb-2 z-10">
              <div className="text-xs uppercase tracking-widest opacity-70">
                {selected.kind === "volume" && "Том"}
                {selected.kind === "section" && "Раздел"}
                {selected.kind === "subsection" && "Подраздел"}
                {selected.kind === "page" && "Страница"}
              </div>
              <div className="flex gap-2">
                {selected.kind === "page" && editor && volumes.length > 0 && (() => {
                  const vol = volumes.find((v) => v.id === editor.volume_id);
                  if (!vol) return null;
                  return (
                    <Button variant="outline" size="sm"
                      onClick={() => window.open(`/book/${encodeURIComponent(vol.slug)}/pages/${editor.id}`, "_blank")}
                      data-testid="book-admin-open-on-platform">
                      Открыть на платформе
                    </Button>
                  );
                })()}
                <Button variant="destructive" size="sm" onClick={removeNode}
                  data-testid="book-admin-delete">
                  <Trash2 className="w-4 h-4 mr-1" /> Удалить
                </Button>
                <Button size="sm" onClick={saveEditor} disabled={savingBusy}
                  data-testid="book-admin-save">
                  {savingBusy ? "Сохранение…" : "Сохранить"}
                </Button>
              </div>
            </div>

            {/* Common fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="block text-xs opacity-70 mb-1">Заголовок</label>
                <Input value={editor.title || ""} onChange={(e) => setEditor({ ...editor, title: e.target.value })}
                  data-testid="book-admin-field-title" />
              </div>
              {selected.kind !== "page" && (
                <details className="md:col-span-1">
                  <summary className="text-xs uppercase tracking-widest opacity-70 cursor-pointer">Slug / URL (авто)</summary>
                  <div className="mt-2">
                    <Input value={editor.slug || ""} onChange={(e) => setEditor({ ...editor, slug: e.target.value })}
                      data-testid="book-admin-field-slug" />
                  </div>
                </details>
              )}
              {selected.kind === "page" && (
                <details className="md:col-span-1">
                  <summary className="text-xs uppercase tracking-widest opacity-70 cursor-pointer">Slug (авто)</summary>
                  <div className="mt-2">
                    <Input value={editor.slug || ""} onChange={(e) => setEditor({ ...editor, slug: e.target.value })} />
                  </div>
                </details>
              )}
              <div>
                <label className="block text-xs opacity-70 mb-1">Подзаголовок</label>
                <Input value={editor.subtitle || ""} onChange={(e) => setEditor({ ...editor, subtitle: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs opacity-70 mb-1">Статус</label>
                <Select value={editor.status || "draft"}
                  onValueChange={(v) => setEditor({ ...editor, status: v })}>
                  <SelectTrigger data-testid="book-admin-field-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Черновик</SelectItem>
                    <SelectItem value="review">На проверке</SelectItem>
                    <SelectItem value="scheduled">Запланировано</SelectItem>
                    <SelectItem value="published">Опубликовано</SelectItem>
                    <SelectItem value="hidden">Скрыто</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {selected.kind === "page" && editor.status === "scheduled" && (
                <div className="md:col-span-2">
                  <label className="block text-xs opacity-70 mb-1">
                    Опубликовать (Europe/Vilnius)
                  </label>
                  <Input
                    type="datetime-local"
                    value={editor._publish_at_local || ""}
                    onChange={(e) => {
                      const local = e.target.value; // "2026-02-25T10:00"
                      // Interpret local as Vilnius time; convert to UTC ISO.
                      // We approximate by using Intl.DateTimeFormat trick:
                      // The user's browser might be in any TZ, so we can't just new Date(local).
                      // Vilnius is UTC+2 (or +3 DST). We'll take advantage of a helper.
                      const utcIso = vilniusLocalToUtcIso(local);
                      setEditor({
                        ...editor,
                        _publish_at_local: local,
                        publish_at: utcIso,
                        publish_timezone: "Europe/Vilnius",
                      });
                    }}
                    data-testid="book-admin-field-publish-at" />
                  <div className="text-[11px] opacity-60 mt-1">
                    Хранится в UTC. Отображение — Europe/Vilnius.
                  </div>
                </div>
              )}
              <div>
                <label className="block text-xs opacity-70 mb-1">Порядок (sort_order)</label>
                <Input type="number" value={editor.sort_order ?? 0}
                  onChange={(e) => setEditor({ ...editor, sort_order: parseInt(e.target.value || "0", 10) })} />
              </div>
              {selected.kind === "page" && (
                <div className="md:col-span-2">
                  <label className="block text-xs opacity-70 mb-1">Макет страницы</label>
                  <Select value={editor.layout || "A"}
                    onValueChange={(v) => setEditor({ ...editor, layout: v })}>
                    <SelectTrigger data-testid="book-admin-field-layout"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAGE_LAYOUTS.map((l) => (
                        <SelectItem key={l.key} value={l.key}>
                          {l.label} — {l.hint}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Volume-specific */}
            {selected.kind === "volume" && (
              <div className="mt-6 space-y-4">
                <div>
                  <label className="block text-xs opacity-70 mb-1">Ключевая цитата (hero_quote)</label>
                  <textarea rows={2} value={editor.hero_quote || ""}
                    onChange={(e) => setEditor({ ...editor, hero_quote: e.target.value })}
                    className="w-full border rounded px-2 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-xs opacity-70 mb-1">Вступление на entry-экране</label>
                  <textarea rows={3} value={editor.entry_intro || ""}
                    onChange={(e) => setEditor({ ...editor, entry_intro: e.target.value })}
                    className="w-full border rounded px-2 py-1.5 text-sm" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs opacity-70 mb-1">URL мобильного мастер-визуала</label>
                    <Input value={editor.entry_mobile_image_url || ""}
                      onChange={(e) => setEditor({ ...editor, entry_mobile_image_url: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs opacity-70 mb-1">URL десктопного мастер-визуала (16:9)</label>
                    <Input value={editor.entry_desktop_image_url || ""}
                      onChange={(e) => setEditor({ ...editor, entry_desktop_image_url: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs opacity-70 mb-1">Product ID (для access-gate)</label>
                  <Input value={editor.product_id || ""}
                    onChange={(e) => setEditor({ ...editor, product_id: e.target.value })} />
                </div>
              </div>
            )}

            {/* Section / Subsection */}
            {(selected.kind === "section" || selected.kind === "subsection") && (
              <div className="mt-6">
                <label className="block text-xs opacity-70 mb-1">Описание</label>
                <textarea rows={4} value={editor.description || ""}
                  onChange={(e) => setEditor({ ...editor, description: e.target.value })}
                  className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
            )}

            {/* Page-specific: layout-driven blocks */}
            {selected.kind === "page" && (
              <div className="mt-6 space-y-5">
                {/* Hero image (all layouts except D uses it optionally) */}
                <div>
                  <div className="text-sm font-medium mb-1">Главное изображение</div>
                  <ImagePicker value={editor.hero_image}
                    onChange={(v) => setEditor({ ...editor, hero_image: v })}
                    testid="book-admin-hero-image" />
                </div>

                {/* Body — used by A, B, C, D */}
                {["A", "B", "C", "D"].includes(editor.layout) && (
                  <div>
                    <label className="block text-xs opacity-70 mb-1">Основной текст (пустая строка = новый абзац)</label>
                    <textarea rows={10} value={editor.body_md || ""}
                      onChange={(e) => setEditor({ ...editor, body_md: e.target.value })}
                      className="w-full border rounded px-2 py-2 text-sm font-serif leading-relaxed"
                      data-testid="book-admin-field-body" />
                  </div>
                )}

                {["A", "B", "C", "D"].includes(editor.layout) && (
                  <div>
                    <label className="block text-xs opacity-70 mb-1">Pull-quote (акцентная фраза)</label>
                    <Input value={editor.pull_quote || ""}
                      onChange={(e) => setEditor({ ...editor, pull_quote: e.target.value })} />
                  </div>
                )}

                {["A", "B", "C", "D"].includes(editor.layout) && (
                  <div>
                    <div className="text-sm font-medium mb-1">Второе изображение (необязательно)</div>
                    <ImagePicker value={editor.secondary_image}
                      onChange={(v) => setEditor({ ...editor, secondary_image: v })}
                      testid="book-admin-secondary-image" />
                  </div>
                )}

                {editor.layout === "E" && (
                  <RecipeEditor value={editor.recipe}
                    onChange={(v) => setEditor({ ...editor, recipe: v })} />
                )}

                {editor.layout === "F" && (
                  <TableEditor value={editor.table}
                    onChange={(v) => setEditor({ ...editor, table: v })} />
                )}

                <div>
                  <label className="block text-xs opacity-70 mb-1">Видео URL (YouTube / Vimeo / mp4)</label>
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-2">
                    <Input value={editor.video_url || ""}
                      onChange={(e) => setEditor({ ...editor, video_url: e.target.value })}
                      placeholder="https://..." />
                    <Select value={editor.video_provider || "external"}
                      onValueChange={(v) => setEditor({ ...editor, video_provider: v })}>
                      <SelectTrigger><SelectValue placeholder="Провайдер" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="youtube">YouTube</SelectItem>
                        <SelectItem value="vimeo">Vimeo</SelectItem>
                        <SelectItem value="mp4">MP4</SelectItem>
                        <SelectItem value="external">Другое</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs opacity-70 mb-1">Поисковые ключевые слова</label>
                  <Input value={editor.search_keywords || ""}
                    onChange={(e) => setEditor({ ...editor, search_keywords: e.target.value })} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
